import { ArrowLeft28Regular, DismissRegular } from "@fluentui/react-icons";
import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, InlineDrawer, OverlayDrawer, mergeClasses } from "@fluentui/react-components";
import React, { useMemo } from "react";

import { M365PulseDrawerProps } from "./M365PulseDrawer.types";
import { observer } from "mobx-react";
import { useDrawerResize } from "./useDrawerResize";
import { useM365PulseDrawerStyles } from "./M365PulseDrawer.styles";
import { useSmallScreen } from "Shared/Hooks";

const M365PulseDrawerBase = (props: M365PulseDrawerProps) => {
    const {
        type = "overlay",
        size = "medium",
        width,
        isOpen = false,
        showLeftNavigation = false,
        showDismiss = true,
        position = "end",
        title,
        onRequestClose,
        onLeftNavigation,
        children,
        hideHeader,
        modalType,
        mountNode,
        footerActions
    } = props;

    const isSmallScreen = useSmallScreen();
    const classNames = useM365PulseDrawerStyles({ showDismiss: showDismiss });
    const convertedType = isSmallScreen ? "overlay" : type;
    const convertedSize = isSmallScreen && position === "end" ? "full" : size;
    const rootClass = mergeClasses(classNames.root, props.className, convertedType === "overlay" && classNames.overlay);

    const resizable = !isSmallScreen && position !== "bottom" && size !== "full";

    const { drawerRef, drawerWidth, isResizing, startResizing, onKeyDown } = useDrawerResize(isOpen && resizable, position);

    const customStyle = useMemo(() => {
        if (convertedSize === "full") {
            return { width: "100vw" };
        }
        if (drawerWidth) {
            return { width: `${drawerWidth}px` };
        }
        if (width) {
            return position === "start" ? { width: width, left: 0 } : { width: width };
        }
        return {};
    }, [convertedSize, drawerWidth, width, position]);

    const DrawerComponent = convertedType === "overlay" ? OverlayDrawer : InlineDrawer;

    const titleComponent = useMemo(() => {
        if (typeof title === "string") {
            return <DrawerHeaderTitle>{title}</DrawerHeaderTitle>;
        }
        return title;
    }, [title]);

    return (
        <DrawerComponent
            separator={props.separator}
            open={isOpen}
            size={convertedSize}
            onOpenChange={onRequestClose}
            className={rootClass}
            position={position}
            ref={drawerRef}
            style={customStyle}
            modalType={modalType}
            mountNode={mountNode}
        >
            {resizable && (
                <div
                    className={mergeClasses(
                        classNames.resizer,
                        isResizing && classNames.resizerActive,
                        position === "end" ? classNames.resizerEnd : classNames.resizerStart
                    )}
                    onMouseDown={startResizing}
                    onKeyDown={onKeyDown}
                    role="separator"
                    tabIndex={0}
                    aria-orientation="vertical"
                    aria-valuenow={drawerWidth ?? drawerRef.current?.clientWidth ?? 400}
                    aria-valuemin={0}
                    aria-valuemax={2500}
                    aria-label="resizer"
                />
            )}
            {!hideHeader && (
                <DrawerHeader className={classNames.drawerHeader}>
                    {showLeftNavigation && (
                        <div className={classNames.leftNavigation}>
                            <Button aria-label="Back" appearance="subtle" icon={<ArrowLeft28Regular />} onClick={onLeftNavigation ?? onRequestClose} />
                        </div>
                    )}
                    <div className={classNames.title}>{titleComponent}</div>
                    <div className={classNames.rightDismiss}>
                        {showDismiss && <Button appearance="transparent" icon={<DismissRegular />} onClick={onRequestClose} aria-label="close side panel" />}
                    </div>
                </DrawerHeader>
            )}
            <DrawerBody className={classNames.body}>{children}</DrawerBody>
            {footerActions && <DrawerFooter className={classNames.footer}>{footerActions}</DrawerFooter>}
        </DrawerComponent>
    );
};

export const M365PulseDrawer = observer(M365PulseDrawerBase);
